/**
 * src/components/ReportDetailModal.tsx
 * 日報詳細モーダル（admin / assistant 共通）
 *
 * ・新フォーマット（work_details）と旧フォーマット（stage/pages_done）両対応
 * ・モバイル: 画面下から BottomSheet 表示
 */

import { useState, useEffect } from 'react'
import {
  STAGE_LABELS,
  KAIKAI_KIKI_LABEL,
  isKaikaiKiki,
  parseWorkDetails,
  legacyToDetail,
  parseImageUrls,
  normalizeImageUrl,
} from './reportUtils'
import { api, type ReportProgressCandidate } from '../api/client'

type Props = {
  report:          Record<string, unknown>
  onClose:         () => void
  isMobile?:       boolean
  isAdmin?:        boolean
  myAssistantId?:  string    // アシスタント自身のID（削除権限判定用）
  onDeleted?:      () => void // 削除完了コールバック
  onEdit?:         (report: Record<string, unknown>) => void // 編集ボタン押下コールバック
}

export function ReportDetailModal({
  report, onClose,
  isMobile = false, isAdmin = false,
  myAssistantId, onDeleted, onEdit,
}: Props) {
  const [lightbox,       setLightbox]       = useState<string | null>(null)
  const [candidates,     setCandidates]     = useState<ReportProgressCandidate[]>([])
  const [candLoading,    setCandLoading]    = useState(false)
  const [applyingIdx,    setApplyingIdx]    = useState<number | null>(null)
  const [confirmDelete,  setConfirmDelete]  = useState(false)
  const [deleting,       setDeleting]       = useState(false)
  const [deleteError,    setDeleteError]    = useState<string | null>(null)

  // 自分の日報かどうか（削除可否判定）。ヘルプスタッフの日報は assistant_id を持たないため常に false。
  const isMyReport = !!myAssistantId && report.assistant_id === myAssistantId
  const isHelp     = report.is_help === true
  const reportId   = report.id ? String(report.id) : ''

  const workDetails = parseWorkDetails(report.work_details) ?? legacyToDetail(report)
  const isNewFormat = !!parseWorkDetails(report.work_details)
  const imageUrls   = parseImageUrls(report.image_urls).map(normalizeImageUrl)

  const issues = typeof report.issues === 'string' ? report.issues : ''
  const handoverNotes = typeof report.handover_notes === 'string' ? report.handover_notes : ''

  // 管理者のみ: 反映候補を取得
  useEffect(() => {
    if (!isAdmin || !reportId) return
    let active = true
    setCandLoading(true)
    api.reportProgressReflections
      .candidates({ daily_report_id: reportId })
      .then((res) => { if (active) setCandidates(res.candidates) })
      .catch(console.error)
      .finally(() => { if (active) setCandLoading(false) })
    return () => { active = false }
  }, [isAdmin, reportId])

  // 候補を再取得する（反映後のリフレッシュ）
  const refreshCandidates = () => {
    if (!isAdmin || !reportId) return
    api.reportProgressReflections
      .candidates({ daily_report_id: reportId })
      .then((res) => setCandidates(res.candidates))
      .catch(console.error)
  }

  const handleApply = async (cand: ReportProgressCandidate) => {
    setApplyingIdx(cand.detail_index)
    try {
      await api.reportProgressReflections.apply({
        source_type:        cand.source_type,
        daily_report_id:    cand.daily_report_id ?? undefined,
        help_work_entry_id: cand.help_work_entry_id ?? undefined,
        help_staff_id:      cand.help_staff_id ?? undefined,
        detail_index:    cand.detail_index,
        episode_id:      cand.episode_id,
        stage_key:       cand.stage_key,
        pages_delta:     cand.pages_delta,
        work_id:         cand.work_id,
        note:            '日報から反映',
      })
      refreshCandidates()
    } catch (err) {
      alert((err as Error).message)
    } finally {
      setApplyingIdx(null)
    }
  }

  return (
    <div
      className="overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ alignItems: isMobile ? 'flex-end' : 'center' }}
    >
      {/* ライトボックス */}
      {lightbox && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300,
          }}
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} style={{ maxWidth: '96vw', maxHeight: '96vh', borderRadius: 6 }} />
        </div>
      )}

      <div
        className="modal"
        style={{
          maxWidth:  isMobile ? '100%' : 580,
          width:     isMobile ? '100%' : undefined,
          margin:    isMobile ? 0 : undefined,
          borderRadius: isMobile ? '14px 14px 0 0' : undefined,
          maxHeight: isMobile ? '90vh' : '85vh',
          overflowY: 'auto',
          ...(isMobile ? { position: 'fixed' as const, bottom: 0, left: 0, right: 0 } : {}),
        }}
      >
        {/* ── ヘッダー ── */}
        <div className="modal-header"
          style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          {isMobile && (
            <div style={{
              position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)',
              width: 36, height: 4, borderRadius: 2, background: 'var(--paper-3)',
            }} />
          )}
          <div style={{ paddingTop: isMobile ? 8 : 0 }}>
            <div className="modal-title">
              {String(report.report_date ?? '')} — {String(report.assistant_name ?? '')}
              {isHelp && <span className="badge badge-blue" style={{ marginLeft: 8, fontSize: 10 }}>ヘルプ</span>}
            </div>
            {!isNewFormat && (
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2 }}>旧フォーマット</div>
            )}
            {isHelp && (
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                ヘルプスタッフの提出内容です（閲覧のみ）。修正は「ヘルプスタッフ管理」画面から行ってください。
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* ── 作業明細 ── */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 10,
              textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>
              作業明細
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {workDetails.map((d, i) => {
                const kk = isKaikaiKiki(d.work_id, d.work_title)
                return (
                  <div key={i} style={{
                    background: kk ? 'var(--amber-light)' : 'var(--paper-2)',
                    borderRadius: 8, padding: '12px 14px',
                    borderLeft: `3px solid ${kk ? 'var(--amber)' : 'var(--blue-light)'}`,
                  }}>
                    {/* 作品・話数・工程・ページ数 */}
                    <div style={{
                      display: 'flex', gap: 8, flexWrap: 'wrap',
                      marginBottom: d.memo ? 8 : 0, alignItems: 'center',
                    }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>
                        {d.work_title || KAIKAI_KIKI_LABEL}
                      </span>
                      {!kk && d.episode_number !== '' && d.episode_number != null && (
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-3)' }}>
                          #{d.episode_number}
                        </span>
                      )}
                      {!kk && d.stage && (
                        <span className="badge badge-gray" style={{ fontSize: 10 }}>
                          {STAGE_LABELS[d.stage] ?? d.stage}
                        </span>
                      )}
                      {!kk && d.pages_done !== '' && d.pages_done != null && Number(d.pages_done) > 0 && (
                        <span style={{
                          background: 'var(--green-light)', color: 'var(--green)',
                          borderRadius: 4, padding: '1px 7px',
                          fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600,
                        }}>
                          {d.pages_done}P
                        </span>
                      )}
                      {kk && (
                        <span className="badge badge-amber" style={{ fontSize: 10 }}>
                          カイカイキキ業務
                        </span>
                      )}
                    </div>
                    {/* 作業内容 */}
                    {d.memo && (
                      <div style={{
                        fontSize: 13, color: 'var(--ink-2)',
                        lineHeight: 1.7, whiteSpace: 'pre-wrap',
                      }}>
                        {d.memo}
                      </div>
                    )}
                    {/* 作業データ保存先（この作業行のもの） */}
                    {d.work_data_path && (
                      <div style={{
                        fontSize: 12, color: 'var(--ink-3)', marginTop: 8,
                        paddingTop: 8, borderTop: '1px dashed var(--paper-3)',
                        wordBreak: 'break-all',
                      }}>
                        📁 保存先：<span style={{ fontFamily: 'var(--font-mono)' }}>{d.work_data_path}</span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* ── 工程進捗への反映候補（管理者のみ） ── */}
          {isAdmin && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 10,
                textTransform: 'uppercase', letterSpacing: '0.08em',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                工程進捗への反映候補
                {candLoading && <span style={{ fontSize: 10, opacity: 0.6 }}>読込中…</span>}
                {!candLoading && candidates.length > 0 && (
                  <span style={{
                    background: candidates.some((cand) => !cand.reflected) ? 'var(--blue-light)' : 'var(--paper-3)',
                    color:      candidates.some((cand) => !cand.reflected) ? 'var(--blue)' : 'var(--ink-3)',
                    borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700,
                  }}>
                    未反映 {candidates.filter((cand) => !cand.reflected).length} 件
                  </span>
                )}
              </div>

              {!candLoading && candidates.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>
                  反映対象の明細がありません
                </div>
              )}

              {candidates.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {candidates.map((cand) => {
                    const isApplying = applyingIdx === cand.detail_index
                    return (
                      <div key={`${cand.detail_index}`} style={{
                        background:  cand.reflected ? 'var(--paper-2)' : '#f0f6ff',
                        borderRadius: 8,
                        padding:     '10px 14px',
                        borderLeft:  `3px solid ${cand.reflected ? 'var(--paper-3)' : 'var(--blue)'}`,
                        opacity:     cand.reflected ? 0.7 : 1,
                      }}>
                        {/* 作品・話数・工程 */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>{cand.work_title}</span>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-3)' }}>
                            #{cand.episode_number}
                          </span>
                          <span className="badge badge-gray" style={{ fontSize: 10 }}>
                            {cand.stage_label}
                          </span>
                        </div>
                        {/* 進捗数字 */}
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, marginBottom: 8 }}>
                          <span>
                            <span style={{ color: 'var(--ink-3)' }}>日報ページ数：</span>
                            <strong style={{ fontFamily: 'var(--font-mono)' }}>{cand.pages_delta}P</strong>
                          </span>
                          <span>
                            <span style={{ color: 'var(--ink-3)' }}>現在進捗：</span>
                            <span style={{ fontFamily: 'var(--font-mono)' }}>
                              {cand.current_progress_pages} / {cand.target_pages}P
                            </span>
                          </span>
                          <span>
                            <span style={{ color: 'var(--ink-3)' }}>反映後：</span>
                            <strong style={{ fontFamily: 'var(--font-mono)', color: cand.reflected ? 'var(--ink-3)' : 'var(--blue)' }}>
                              {cand.projected_progress_pages} / {cand.target_pages}P
                            </strong>
                          </span>
                        </div>
                        {/* ボタン */}
                        {cand.reflected ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            fontSize: 12, color: 'var(--ink-3)',
                            background: 'var(--paper-3)', borderRadius: 6, padding: '3px 10px',
                          }}>
                            ✓ 反映済み
                          </span>
                        ) : (
                          <button
                            className="btn btn-sm btn-primary"
                            style={{ fontSize: 12 }}
                            disabled={isApplying}
                            onClick={() => handleApply(cand)}
                          >
                            {isApplying ? '反映中…' : '工程進捗へ反映'}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── 問題点 ── */}
          {issues && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 11, fontWeight: 600, color: 'var(--red)', marginBottom: 6,
                textTransform: 'uppercase', letterSpacing: '0.08em',
              }}>
                ⚠ 問題点・詰まったこと
              </div>
              <div style={{
                fontSize: 13, lineHeight: 1.7,
                background: '#fde8e6', borderRadius: 6, padding: '10px 14px',
                whiteSpace: 'pre-wrap', color: 'var(--ink)',
              }}>
                {String(report.issues)}
              </div>
            </div>
          )}

          {/* ── 引継ぎ・連絡事項 ── */}
          {handoverNotes && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 11, fontWeight: 600, color: 'var(--amber)', marginBottom: 6,
                textTransform: 'uppercase', letterSpacing: '0.08em',
              }}>
                引継ぎ・連絡事項
              </div>
              <div style={{
                fontSize: 13, lineHeight: 1.7,
                background: 'var(--amber-light)', borderRadius: 6, padding: '10px 14px',
                whiteSpace: 'pre-wrap', color: 'var(--ink)',
              }}>
                {String(report.handover_notes)}
              </div>
            </div>
          )}

          {/* ── 画像 ── */}
          {imageUrls.length > 0 && (
            <div>
              <div style={{
                fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8,
                textTransform: 'uppercase', letterSpacing: '0.08em',
              }}>
                作業画像 ({imageUrls.length}枚) — クリック/タップで拡大
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                gap: 8,
              }}>
                {imageUrls.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    onClick={() => setLightbox(url)}
                    style={{
                      width: '100%', aspectRatio: '1', objectFit: 'cover',
                      borderRadius: 8, cursor: 'pointer', border: '1px solid var(--paper-3)',
                    }}
                    onError={(e) => { (e.target as HTMLImageElement).style.background = 'var(--paper-3)' }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 自分の日報の場合のみ削除導線を表示（非admin）。ヘルプ日報は編集・削除不可（閲覧のみ） */}
        {!isAdmin && isMyReport && !isHelp && reportId && (
          <div style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--paper-3)',
          }}>
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => { setDeleteError(null); setConfirmDelete(true) }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--ink-3)', fontSize: 12, padding: 0,
                  textDecoration: 'underline',
                }}
              >
                この日報を削除
              </button>
            ) : (
              <div style={{
                background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
                borderRadius: 8, padding: '12px 14px',
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  この日報を削除します。
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10, lineHeight: 1.6 }}>
                  日付を間違えた場合は、削除後に正しい日付で再提出してください。
                  <br />よろしいですか？
                </div>
                {deleteError && (
                  <div style={{
                    fontSize: 12, color: 'var(--red)', background: '#fde8e6',
                    borderRadius: 6, padding: '8px 10px', marginBottom: 10,
                  }}>
                    ⚠ {deleteError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-sm"
                    style={{ background: 'var(--red)', color: '#fff', border: 'none', cursor: 'pointer' }}
                    disabled={deleting}
                    onClick={async () => {
                      setDeleting(true); setDeleteError(null)
                      try {
                        await api.reports.deleteOwn(reportId)
                        onDeleted?.()
                        onClose()
                      } catch (ex: any) {
                        setDeleteError(ex.message ?? '削除に失敗しました')
                        setConfirmDelete(false)
                      } finally { setDeleting(false) }
                    }}
                  >
                    {deleting ? '削除中...' : '削除する'}
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => { setConfirmDelete(false); setDeleteError(null) }}
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="modal-footer"
          style={{ position: 'sticky', bottom: 0, background: '#fff' }}>
          {!isAdmin && isMyReport && !isHelp && onEdit && (
            <button
              className="btn btn-outline"
              onClick={() => { onClose(); onEdit(report) }}
              style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 48 : undefined }}
            >
              ✏ 編集
            </button>
          )}
          <button
            className="btn btn-outline"
            onClick={onClose}
            style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 48 : undefined }}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  )
}